import { useAuthenticator } from '@aws-amplify/ui-react'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import { GONE, OK } from 'http-status'
import {
  CSSProperties,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import PrimaryButton from '../../../../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../../../../global/components/buttons/secondary-button/SecondaryButton'
import ServiceDropDown from '../../../../../../global/components/service-drop-down/ServiceDropDown'
import { TYPES } from '../../../../../../global/constants/invoice'
import { STATUSES as ORDER_STATUSES } from '../../../../../../global/constants/order-status'
import {
  MODES,
  SCHEDULING_KEYS,
  TITLES,
} from '../../../../../../global/constants/scheduler'
import { fetchData } from '../../../../../../global/utils/fetch'
import getUsername from '../../../../../../global/utils/getUsername'
import { AppointmentSlots } from '../../../../../../schedule/components/schedule-modal/components/appointment-slot/AppointmentSlots'
import { TireSizeQuantity } from '../../../../../../schedule/components/schedule-modal/components/choose-service/TireSizeQuantity'
import { updateOrderStatusToScheduled } from '../../../../../../schedule/components/schedule-modal/components/choose-service/api'
import { ConfirmAppointment } from '../../../../../../schedule/components/schedule-modal/components/confirm-appointment/ConfirmAppointment'
import { CustomerInformation } from '../../../../../../schedule/components/schedule-modal/components/customer-information/CustomerInformation'
import { PaymentInformation } from '../../../../../../schedule/components/schedule-modal/components/payment-information/PaymentInformation'
import { WoDetailContext } from '../../../../context/context'
import styles from '../../scheduling-modal/styles.module.css'
import transformOrderIntoSchedulerState from './transform-order-data-into-scheduler-state'
import isTransactionProcessing from '../../../../../../global/utils/invoice/status/is-transaction-processing'
import { Invoice } from '../../../../../../../../app/entities/Invoice'
import isTransactionSettled from '../../../../../../global/utils/invoice/status/is-transaction-settled'
import ServicePriceOverride from '../../../../../../global/components/service-price-override/ServicePriceOverride'
import {
  getInitialStateProps,
  ISchedulerStateProps,
} from '../../../../../../schedule/components/schedule-modal/components/container-modal/initialData'
import removeFreeOrderReasonAndCalculateCosts from '../../../../../../global/utils/remove-free-order-reason-and-calculate-costs'
import { finishFlow } from '../../../../../../schedule/components/schedule-modal/components/container-modal/scheduleRoutes'

interface IScheduleModalProps {
  isOpen: boolean
  handleClosed: Function
  currentPage: SCHEDULING_KEYS
  setCurrentPage: Dispatch<SetStateAction<SCHEDULING_KEYS>>
  mode: MODES
  disableSchedulerNavigation: boolean
  isOrderCompleted: boolean
  rescheduleReason?: string
}

export default function ScheduleContainerModal(props: IScheduleModalProps) {
  const {
    handleClosed,
    isOpen,
    currentPage,
    mode,
    setCurrentPage,
    disableSchedulerNavigation,
    isOrderCompleted,
    rescheduleReason,
  } = props
  const { data: order, queryOrderData } = useContext(WoDetailContext)
  const { user } = useAuthenticator((context) => [context.user])
  const [editOrderState, setEditOrderState] = useState<ISchedulerStateProps>(
    getInitialStateProps(),
  )
  const [affirmativeDisabled, setAffirmativeDisabled] = useState(false)
  const [isStateInitialized, setIsStateInitialized] = useState(false)

  // Re-initialize scheduler state whenever modal opens or order changes
  useEffect(() => {
    if (isOpen) {
      setEditOrderState(transformOrderIntoSchedulerState(order))
      setIsStateInitialized(true)
    } else {
      // Reset initialization flag when modal closes
      setIsStateInitialized(false)
    }
  }, [isOpen, order])

  function onScheduleContainerModalClose() {
    setEditOrderState(getInitialStateProps())
    setCurrentPage(SCHEDULING_KEYS.customerInformation)
    handleClosed()
  }

  useEffect(() => {
    const orderExistsAndIsFree =
      editOrderState.orderId && !editOrderState.freeOrderReason.isFree
    if (orderExistsAndIsFree) {
      removeFreeOrderReasonAndCalculateCosts(editOrderState, setEditOrderState)
    }
  }, [editOrderState.freeOrderReason.isFree])

  const [priceOverrideIsDisabled, setPriceOverrideIsDisabled] = useState(true)

  const isOrderStatusCompleted = order.status === ORDER_STATUSES.completed
  const hasSettledInvoice = Boolean(
    order.invoices.find((invoice: Invoice) => {
      return (
        !invoice.tip &&
        invoice.type === TYPES.COMPLETED &&
        isTransactionSettled(invoice)
      )
    }),
  )
  const hasSubmittedForSettlementInvoice = Boolean(
    order.invoices.find((invoice: Invoice) => {
      return (
        !invoice.tip &&
        invoice.type === TYPES.COMPLETED &&
        isTransactionProcessing(invoice)
      )
    }),
  )

  useEffect(() => {
    setPriceOverrideIsDisabled(
      editOrderState.servicePriceOverride === undefined,
    )
  }, [])

  const ref = useRef<any>(null)

  const nextClick = useCallback(async () => {
    if (mode === MODES.FINISH) {
      await ref.current.handleNextClick((goToNextPage: boolean) => {
        if (goToNextPage) {
          const nextPage = finishFlow[currentPage]
          setCurrentPage(nextPage)
        } else {
          alert(
            'Something unexpected happened. Please check your inputs and try again.',
          )
        }
      })
    }
  }, [mode, currentPage])

  const confirmClick = useCallback(async () => {
    try {
      setAffirmativeDisabled(true)
      // we retrieve a "raw" response because we want to check the status code
      const updateStatusToScheduledRes = await fetchData<Response>(
        updateOrderStatusToScheduled(editOrderState.orderId, {
          reason: rescheduleReason,
          username: getUsername(user),
        }),
      )

      const chosenTimeSlotIsNoLongerAvailable =
        updateStatusToScheduledRes.status === GONE
      if (chosenTimeSlotIsNoLongerAvailable) {
        alert(
          'The time slot you selected is no longer available. Please select another one.',
        )
        setCurrentPage(SCHEDULING_KEYS.appointmentSlot)
        return
      } else if (updateStatusToScheduledRes.status !== OK) {
        throw new Error('Something went wrong. Please try again.')
      }

      await queryOrderData(order.objectId)
      onScheduleContainerModalClose()
    } catch (error) {
      console.error('An error occurred:', error)
      alert(error)
    } finally {
      setAffirmativeDisabled(false)
      onScheduleContainerModalClose()
    }
  }, [editOrderState, order, queryOrderData, rescheduleReason, user])

  const saveClick = useCallback(async () => {
    if (ref.current === null) return
    try {
      await ref.current.handleSaveClick()
      await queryOrderData(order.objectId)
      onScheduleContainerModalClose()
    } catch (error) {
      console.error('An error occurred:', error)
    }
  }, [queryOrderData, order])

  const attemptPaymentClick = useCallback(async () => {
    if (ref.current === null) return

    const paymentProcessed: boolean =
      await ref.current.handleAttemptPaymentClick()
    if (paymentProcessed) await queryOrderData(order.objectId)
  }, [queryOrderData, order])

  function goBack() {
    if (mode === MODES.FINISH) {
      const reversedFlow = Object.fromEntries(
        Object.entries(finishFlow).map(([key, value]) => [value, key]),
      )
      const nextSchedulingPage = reversedFlow[currentPage]
      setCurrentPage(nextSchedulingPage as SCHEDULING_KEYS)
      setAffirmativeDisabled(false)
    } else {
      console.error('NO FLOW FOR THIS BUTTON WHEN NOT IN `FINISH` MODE')
    }
  }

  const [affirmativeButton, setAffirmativeButton] = useState<any>(
    <PrimaryButton
      buttonName={mode === MODES.FINISH ? 'Next' : 'Save Changes'}
      disabled={affirmativeDisabled}
      onClick={mode === MODES.FINISH ? nextClick : saveClick}
    />,
  )

  const whichAffirmativeButton = useCallback(
    (isCardOnFile?: boolean) => {
      // only visible during finish flow
      if (currentPage === SCHEDULING_KEYS.confirmAppointment) {
        setAffirmativeButton(
          <PrimaryButton
            buttonName={'Confirm'}
            disabled={affirmativeDisabled}
            onClick={confirmClick}
          />,
        )
      }
      // only visible during finish flow
      else if (mode === MODES.FINISH) {
        setAffirmativeButton(
          <PrimaryButton
            buttonName={'Next'}
            disabled={affirmativeDisabled}
            onClick={nextClick}
          />,
        )
      }
      // we will allow the rep to attempt payments if the order is completed and it does not have a paid invoice
      // and they are on the payment information page
      else if (
        currentPage === SCHEDULING_KEYS.paymentInformation &&
        !hasSettledInvoice &&
        isOrderStatusCompleted
      ) {
        const shouldDisableAttempt =
          !isCardOnFile ||
          hasSubmittedForSettlementInvoice ||
          affirmativeDisabled
        setAffirmativeButton(
          <PrimaryButton
            buttonName={'Attempt Payment'}
            disabled={shouldDisableAttempt}
            onClick={attemptPaymentClick}
          />,
        )
      } else {
        const shouldDisableBecauseNoCardOnFile =
          currentPage === SCHEDULING_KEYS.paymentInformation && !isCardOnFile
        const isSaveChangesDisabled =
          shouldDisableBecauseNoCardOnFile || affirmativeDisabled
        setAffirmativeButton(
          <PrimaryButton
            buttonName={'Save Changes'}
            disabled={isSaveChangesDisabled}
            onClick={saveClick}
          />,
        )
      }
    },
    [
      mode,
      hasSettledInvoice,
      isOrderStatusCompleted,
      affirmativeDisabled,
      hasSubmittedForSettlementInvoice,
      saveClick,
      attemptPaymentClick,
      nextClick,
      confirmClick,
    ],
  )

  useEffect(() => {
    whichAffirmativeButton()
  }, [
    mode,
    hasSettledInvoice,
    isOrderStatusCompleted,
    isOrderCompleted,
    affirmativeDisabled,
    whichAffirmativeButton,
  ])

  function isCardOnFileCallback(isCardOnFile: boolean) {
    whichAffirmativeButton(isCardOnFile)
  }

  function renderPage() {
    switch (currentPage) {
      case SCHEDULING_KEYS.customerInformation:
        return (
          <CustomerInformation
            ref={ref}
            schedulerState={editOrderState}
            setSchedulerState={setEditOrderState}
            setDisabled={setAffirmativeDisabled}
            mode={mode}
          />
        )
      case SCHEDULING_KEYS.chooseAService:
        return (
          <TireSizeQuantity
            ref={ref}
            schedulerState={editOrderState}
            setSchedulerState={setEditOrderState}
            setDisabled={setAffirmativeDisabled}
            order={order}
            mode={mode}
          />
        )
      case SCHEDULING_KEYS.appointmentSlot:
        return (
          <AppointmentSlots
            ref={ref}
            schedulerState={editOrderState}
            setSchedulerState={setEditOrderState}
            setDisabled={setAffirmativeDisabled}
            mode={mode}
            rescheduleReason={rescheduleReason}
          />
        )
      case SCHEDULING_KEYS.paymentInformation:
        return (
          <PaymentInformation
            ref={ref}
            schedulerState={editOrderState}
            setSchedulerState={setEditOrderState}
            setDisabled={setAffirmativeDisabled}
            mode={mode}
            isCardOnFileCallback={isCardOnFileCallback}
          />
        )
      case SCHEDULING_KEYS.confirmAppointment:
        return (
          <ConfirmAppointment
            schedulerState={editOrderState}
            setSchedulerState={setEditOrderState}
            setDisabled={setAffirmativeDisabled}
            mode={mode}
          />
        )
    }
  }

  function getNavButtonDisabledStyles(): CSSProperties | undefined {
    return isOrderCompleted
      ? { color: '#767676', pointerEvents: 'none' }
      : { color: '#d70000' }
  }

  const displayConfirmApp =
    order.status === ORDER_STATUSES.quote && order.tireRackOrders
  return (
    <div>
      <Modal open={isOpen}>
        <Box className={styles.scheduleModal}>
          <div className={`${styles.scheduleModalPageTitle} font--bold`}>
            {TITLES[currentPage]}
            {currentPage === SCHEDULING_KEYS.chooseAService && (
              <>
                <ServiceDropDown
                  mode={MODES.EDIT}
                  defaultValue={editOrderState.service}
                  onServiceChange={(newValue: string) => {
                    setEditOrderState((prevState) => ({
                      ...prevState,
                      service: newValue,
                    }))
                  }}
                />
                <ServicePriceOverride
                  schedulerState={editOrderState}
                  setSchedulerState={setEditOrderState}
                  priceOverrideIsDisabled={priceOverrideIsDisabled}
                  setPriceOverrideIsDisabled={setPriceOverrideIsDisabled}
                />
              </>
            )}
          </div>
          <div
            style={{
              minHeight: '500px',
              width: '100%',
              overflowX: 'auto',
              height: '1040px',
            }}
          >
            {isStateInitialized ? renderPage() : <div>Loading...</div>}
            {/* without the isStateInitialized flag we will cause a race condition between the useEffect 
            setting the schedulerState and the renderPage */}
          </div>

          <div className={styles.bottomNavBar}>
            <div className={styles.buttonContainer}>
              {mode !== MODES.FINISH && (
                <div
                  className={`${styles.pagesLinks} ${
                    disableSchedulerNavigation ? styles.buttonDisabled : null
                  }`}
                >
                  <button
                    className={`${styles.navLink} ${
                      currentPage === SCHEDULING_KEYS.customerInformation
                        ? styles.active
                        : null
                    }`}
                    disabled={isOrderCompleted}
                    style={getNavButtonDisabledStyles()}
                    onClick={() =>
                      setCurrentPage(SCHEDULING_KEYS.customerInformation)
                    }
                  >
                    Customer Information
                  </button>
                  <button
                    className={`${styles.navLink} ${
                      currentPage === SCHEDULING_KEYS.chooseAService
                        ? styles.active
                        : null
                    }`}
                    disabled={isOrderCompleted}
                    style={getNavButtonDisabledStyles()}
                    onClick={() =>
                      setCurrentPage(SCHEDULING_KEYS.chooseAService)
                    }
                  >
                    Choose a Service
                  </button>
                  <button
                    className={`${styles.navLink} ${
                      currentPage === SCHEDULING_KEYS.appointmentSlot
                        ? styles.active
                        : styles.buttonDisabled
                    }`}
                    disabled={true}
                    style={{ color: 'var(--text-secondary)', pointerEvents: 'none' }}
                    onClick={() =>
                      setCurrentPage(SCHEDULING_KEYS.appointmentSlot)
                    }
                  >
                    Appointment Slot
                  </button>
                  <button
                    className={`${styles.navLink} ${
                      currentPage === SCHEDULING_KEYS.paymentInformation
                        ? styles.active
                        : null
                    }`}
                    onClick={() =>
                      setCurrentPage(SCHEDULING_KEYS.paymentInformation)
                    }
                  >
                    Payment Information
                  </button>
                  {displayConfirmApp && (
                    <button
                      className={`${styles.navLink} ${
                        currentPage === SCHEDULING_KEYS.confirmAppointment
                          ? styles.active
                          : null
                      }`}
                      onClick={() =>
                        setCurrentPage(SCHEDULING_KEYS.confirmAppointment)
                      }
                    >
                      Confirm Appointment
                    </button>
                  )}
                </div>
              )}
              <div className={styles.flexButtons}>
                <div className={styles.flexGroupButtons}>
                  {mode === MODES.FINISH &&
                    currentPage !== SCHEDULING_KEYS.confirmAppointment && (
                      <SecondaryButton
                        buttonName='Back'
                        onClick={() => goBack()}
                      />
                    )}
                  <SecondaryButton
                    buttonName='Cancel'
                    onClick={onScheduleContainerModalClose}
                  />
                </div>
                {affirmativeButton}
              </div>
            </div>
          </div>
        </Box>
      </Modal>
    </div>
  )
}
